import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CalendarDays, Clock, Users, Loader2, FileSpreadsheet, Hand, Printer, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDateShort = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
  } catch {
    return iso;
  }
};

export default function EventScheduleDialog({ open, onOpenChange, group, onEditStaff, refreshToken }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchSchedule = useCallback(async () => {
    if (!group?.shifts?.length) return;
    setLoading(true);
    try {
      const ids = group.shifts.map((s) => s.id).join(',');
      const res = await axios.get(`${API}/admin/event-schedule`, { params: { open_shift_ids: ids } });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load schedule');
    } finally {
      setLoading(false);
    }
  }, [group]);

  useEffect(() => {
    if (open && group) fetchSchedule();
    if (!open) setData(null);
  }, [open, group, fetchSchedule]);

  // Refetch when a "Set Hours" edit is saved while this dialog stays open.
  useEffect(() => {
    if (open && group && refreshToken) fetchSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const handlePrint = () => window.print();

  if (!group) return null;

  const event = data?.event;
  const staff = data?.staff || [];
  const dates = event?.dates || [];
  const grandTotalHours = staff.reduce((sum, s) => sum + (s.total_hours || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-barlow tracking-wider uppercase">
            <CalendarDays className="w-5 h-5 text-primary" />
            Event Schedule
          </DialogTitle>
          <DialogDescription>
            {event ? (
              <span className="block">
                <strong className="text-amber-300">{event.position || 'Event'}</strong>
                {event.location ? <> — {event.location}</> : null}
                {dates.length > 0 && (
                  <> — {dates.length} day{dates.length !== 1 ? 's' : ''} ({formatDateShort(dates[0])} → {formatDateShort(dates[dates.length - 1])})</>
                )}
              </span>
            ) : (
              'Loading event details…'
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : staff.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">
            <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>No staff assigned to this event yet.</p>
            <p className="text-xs mt-1">Use the Manage button or Import Excel for this event to add staff.</p>
          </div>
        ) : (
          <>
            {/* Top stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
              <div className="border border-border rounded-sm p-2 bg-muted/30">
                <div className="text-2xl font-bold text-foreground">{staff.length}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Staff</div>
              </div>
              <div className="border border-border rounded-sm p-2 bg-muted/30">
                <div className="text-2xl font-bold text-foreground">{dates.length}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Days</div>
              </div>
              <div className="border border-border rounded-sm p-2 bg-muted/30">
                <div className="text-2xl font-bold text-foreground">{staff.reduce((s, x) => s + (x.total_shifts || 0), 0)}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total shifts</div>
              </div>
              <div className="border border-border rounded-sm p-2 bg-muted/30">
                <div className="text-2xl font-bold text-primary">{grandTotalHours.toFixed(1)}h</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total hours</div>
              </div>
            </div>

            {/* Schedule table */}
            <div className="flex-1 overflow-auto border border-border rounded-sm mt-2">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-background border-b border-border">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold sticky left-0 bg-background z-10 min-w-[180px]">Staff</th>
                    {dates.map((d) => (
                      <th key={d} className="text-left px-3 py-2 font-semibold min-w-[140px]">
                        {formatDateShort(d)}
                      </th>
                    ))}
                    <th className="text-right px-3 py-2 font-semibold min-w-[80px]">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {staff.map((s) => (
                    <tr key={s.user_id} className="border-b border-border/40 hover:bg-muted/20">
                      <td className="px-3 py-2 sticky left-0 bg-background z-10">
                        <div className="font-medium flex items-center gap-1.5">
                          <span>{s.user_name || '—'}</span>
                          {onEditStaff && (
                            <button
                              type="button"
                              onClick={() => onEditStaff({ user_id: s.user_id, user_name: s.user_name })}
                              className="text-muted-foreground hover:text-primary transition-colors"
                              title="Edit this person's hours"
                              data-testid={`schedule-edit-${s.user_id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                          {s.employee_code && (
                            <span className="text-[10px] text-muted-foreground font-mono">#{s.employee_code}</span>
                          )}
                          {s.user_role === 'supervisor' && (
                            <Badge variant="outline" className="text-[9px] py-0 h-4 border-blue-500/40 text-blue-300">
                              SUP
                            </Badge>
                          )}
                        </div>
                      </td>
                      {dates.map((d) => {
                        const shifts = s.shifts_by_date?.[d] || [];
                        return (
                          <td key={d} className="px-3 py-2 align-top">
                            {shifts.length === 0 ? (
                              <span className="text-muted-foreground text-xs">—</span>
                            ) : (
                              <div className="space-y-1">
                                {shifts.map((sh, i) => (
                                  <div key={i} className="flex flex-col gap-0.5">
                                    <div className="flex items-center gap-1.5">
                                      <Clock className="w-3 h-3 text-muted-foreground" />
                                      <span className="font-mono text-xs">
                                        {sh.start_time || '?'} – {sh.end_time || '?'}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1 flex-wrap">
                                      {sh.source === 'excel' ? (
                                        <Badge
                                          variant="outline"
                                          className="text-[9px] py-0 h-4 border-green-500/40 text-green-300 bg-green-500/5"
                                          title="Imported from Excel"
                                        >
                                          <FileSpreadsheet className="w-2.5 h-2.5 mr-0.5" />
                                          Excel
                                        </Badge>
                                      ) : (
                                        <Badge
                                          variant="outline"
                                          className="text-[9px] py-0 h-4 border-blue-500/40 text-blue-300 bg-blue-500/5"
                                          title="Added manually"
                                        >
                                          <Hand className="w-2.5 h-2.5 mr-0.5" />
                                          Manual
                                        </Badge>
                                      )}
                                      {sh.is_split && (
                                        <Badge variant="outline" className="text-[9px] py-0 h-4 border-amber-500/40 text-amber-300">
                                          Split
                                        </Badge>
                                      )}
                                      {sh.is_extra && (
                                        <Badge variant="outline" className="text-[9px] py-0 h-4 border-purple-500/40 text-purple-300">
                                          Extra
                                        </Badge>
                                      )}
                                      {sh.end_time && sh.start_time && sh.end_time <= sh.start_time && (
                                        <Badge variant="outline" className="text-[9px] py-0 h-4 border-indigo-500/40 text-indigo-300">
                                          Night
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-right font-mono text-sm">
                        {(s.total_hours || 0).toFixed(1)}h
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-border print:hidden">
          <Button variant="outline" onClick={handlePrint} data-testid="event-schedule-print-btn">
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          <Button onClick={() => onOpenChange?.(false)} className="bg-primary text-primary-foreground hover:bg-yellow-300">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
